import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { formatarCnpj } from '@via-tax/contracts'
import { Aviso, Botao, Entrada, EstadoVazio, Selecao, Selo } from '@via-tax/ui'
import { useState } from 'react'
import { SincronizacaoPortal } from '../components/SincronizacaoPortal'
import { dataBr, reais, ROTULO_MODELO, ROTULO_PAPEL, ROTULO_SITUACAO } from '../formatos'
import type { ContextoSessao } from '../tipos'
import { mensagemDeErro, useTRPC } from '../trpc'

type Filtros = {
  de?: string
  ate?: string
  papel?: 'ENTRADA' | 'SAIDA' | 'INTERNO'
  modelo?: string
  situacao?: 'AUTORIZADO' | 'CANCELADO' | 'DENEGADO' | 'SEM_PROTOCOLO'
  busca?: string
}

const POR_PAGINA = 50

export function DocumentosRecebidos({ contexto, irParaEnvio }: { contexto: ContextoSessao; irParaEnvio: () => void }) {
  const podeEnviar = (contexto.capacidades as string[]).includes('upload_xml')
  const trpc = useTRPC()
  const [filtros, setFiltros] = useState<Filtros>({})
  const [busca, setBusca] = useState('')
  const [pagina, setPagina] = useState(1)
  const lista = useQuery({
    ...trpc.documento.listar.queryOptions({ ...filtros, pagina, porPagina: POR_PAGINA }),
    placeholderData: keepPreviousData,
  })

  const mudar = (p: Partial<Filtros>) => {
    setFiltros((f) => ({ ...f, ...p }))
    setPagina(1)
  }
  const d = lista.data
  const paginas = d ? Math.max(1, Math.ceil(d.total / POR_PAGINA)) : 1
  const semFiltro = !Object.values(filtros).some(Boolean)

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Documentos recebidos</h1>
          {d && (
            <p className="mt-1 text-sm text-tinta-2">
              <span className="num">{d.total.toLocaleString('pt-BR')}</span> documento(s)
              <span className="text-tinta-3"> · autorizados somam </span>
              <span className="num">{reais(d.valorAutorizado)}</span>
            </p>
          )}
        </div>
        {podeEnviar && <Botao onClick={irParaEnvio}>Enviar mais</Botao>}
      </header>

      <SincronizacaoPortal contexto={contexto} />

      <form
        className="grid grid-cols-1 gap-3 rounded-lg border border-linha bg-painel p-3 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-7"
        onSubmit={(e) => {
          e.preventDefault()
          mudar({ busca: busca.trim() || undefined })
        }}
      >
        <label className="flex min-w-0 flex-col gap-1 sm:col-span-2">
          <span className="text-[11px] text-tinta-3">Buscar</span>
          <Entrada className="w-full min-w-0" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Número, chave, nome ou CNPJ" />
        </label>
        <label className="flex min-w-0 flex-col gap-1">
          <span className="text-[11px] text-tinta-3">Operação</span>
          <Selecao className="w-full min-w-0" value={filtros.papel ?? ''} onChange={(e) => mudar({ papel: (e.target.value || undefined) as Filtros['papel'] })}>
            <option value="">Entradas e saídas</option>
            <option value="ENTRADA">Entradas</option>
            <option value="SAIDA">Saídas</option>
            <option value="INTERNO">Entre unidades</option>
          </Selecao>
        </label>
        <label className="flex min-w-0 flex-col gap-1">
          <span className="text-[11px] text-tinta-3">Modelo</span>
          <Selecao className="w-full min-w-0" value={filtros.modelo ?? ''} onChange={(e) => mudar({ modelo: e.target.value || undefined })}>
            <option value="">Todos os modelos</option>
            {Object.entries(ROTULO_MODELO).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Selecao>
        </label>
        <label className="flex min-w-0 flex-col gap-1">
          <span className="text-[11px] text-tinta-3">Competência de</span>
          <Entrada className="w-full min-w-0" type="month" value={filtros.de ?? ''} onChange={(e) => mudar({ de: e.target.value || undefined })} />
        </label>
        <label className="flex min-w-0 flex-col gap-1">
          <span className="text-[11px] text-tinta-3">Competência até</span>
          <Entrada className="w-full min-w-0" type="month" value={filtros.ate ?? ''} onChange={(e) => mudar({ ate: e.target.value || undefined })} />
        </label>
        <label className="flex min-w-0 flex-col gap-1 sm:col-span-2 lg:col-span-2 2xl:col-span-1">
          <span className="text-[11px] text-tinta-3">Situação</span>
          <Selecao className="w-full min-w-0" value={filtros.situacao ?? ''} onChange={(e) => mudar({ situacao: (e.target.value || undefined) as Filtros['situacao'] })}>
            <option value="">Qualquer situação</option>
            {Object.entries(ROTULO_SITUACAO).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Selecao>
        </label>
        {!semFiltro && (
          <div className="flex items-end sm:col-span-2 lg:col-span-1">
            <Botao
              variante="fantasma"
              type="button"
              onClick={() => {
                setFiltros({})
                setBusca('')
                setPagina(1)
              }}
            >
              Limpar filtros
            </Botao>
          </div>
        )}
      </form>

      {d && (d.icms.entradas > 0 || d.icms.saidas > 0 || d.icms.st > 0) && (
        <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Resumo rotulo="ICMS destacado nas entradas" valor={d.icms.entradas} nota="notas autorizadas com os filtros acima" />
          <Resumo rotulo="ICMS destacado nas saídas" valor={d.icms.saidas} nota="notas autorizadas com os filtros acima" />
          <Resumo rotulo="ICMS-ST nas notas" valor={d.icms.st} nota="substituição tributária destacada" />
        </section>
      )}

      {lista.isError && <Aviso>{mensagemDeErro(lista.error)}</Aviso>}

      {d && d.total === 0 ? (
        semFiltro ? (
          <EstadoVazio titulo="Nenhum documento ainda" acao={podeEnviar ? <Botao variante="primario" onClick={irParaEnvio}>Enviar XMLs</Botao> : undefined}>
            As notas de entrada e saída dos últimos 12 meses chegam pelo escritório e pelo Portal do Cliente.
          </EstadoVazio>
        ) : (
          <EstadoVazio titulo="Nenhum documento com esses filtros">Ajuste os filtros acima.</EstadoVazio>
        )
      ) : (
        <div className="overflow-hidden rounded-lg border border-linha bg-painel">
          {/* Celular e tablet: cartões. Tela larga: tabela. Nenhum dos dois rola para o lado. */}
          <ul className={`divide-y divide-linha lg:hidden ${lista.isFetching ? 'opacity-60' : ''}`}>
            {d?.itens.map((doc) => (
              <li key={doc.id} className={`flex flex-col gap-1.5 px-4 py-3 text-[13px] ${doc.situacao === 'CANCELADO' || doc.situacao === 'DENEGADO' ? 'text-tinta-3' : ''}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span>
                    <span className="font-medium">{ROTULO_MODELO[doc.modelo] ?? doc.modelo}</span> <span className="num">{doc.numero}</span>
                    <span className="num ml-2 text-tinta-3">{dataBr(doc.dataEmissao)}</span>
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Selo tom={doc.papel === 'ENTRADA' ? 'navy' : 'neutro'}>{ROTULO_PAPEL[doc.papel]}</Selo>
                    {doc.situacao !== 'AUTORIZADO' && <Selo tom={doc.situacao === 'SEM_PROTOCOLO' ? 'neutro' : 'alerta'}>{ROTULO_SITUACAO[doc.situacao]}</Selo>}
                  </span>
                </div>
                <Contraparte doc={doc} />
                <div className="grid grid-cols-2 gap-2">
                  <span>
                    <span className="block text-[11px] text-tinta-3">Valor</span>
                    <span className="num">{reais(doc.valorTotal)}</span>
                  </span>
                  <span className="text-right">
                    <span className="block text-[11px] text-tinta-3">ICMS</span>
                    <Icms doc={doc} />
                  </span>
                </div>
                <a href={`/documentos/${doc.id}/xml`} className="self-start text-xs text-navy underline-offset-4 hover:underline">
                  Baixar XML
                </a>
              </li>
            ))}
          </ul>

          <table className={`hidden w-full table-fixed text-left text-[13px] lg:table ${lista.isFetching ? 'opacity-60' : ''}`}>
            <colgroup>
              <col className="w-[7.5rem]" />
              <col className="w-[9rem]" />
              <col className="w-[6.5rem]" />
              <col />
              <col className="w-[8rem]" />
              <col className="w-[10.5rem]" />
              <col className="w-[8rem]" />
              <col className="w-[3.5rem]" />
            </colgroup>
            <thead className="border-b border-linha bg-fundo text-[11px] uppercase tracking-[0.06em] text-tinta-3">
              <tr>
                <th className="px-3 py-2.5 font-medium">Emissão</th>
                <th className="px-3 py-2.5 font-medium">Documento</th>
                <th className="px-3 py-2.5 font-medium">Operação</th>
                <th className="px-3 py-2.5 font-medium">Contraparte</th>
                <th className="px-3 py-2.5 text-right font-medium">Valor</th>
                <th className="px-3 py-2.5 text-right font-medium">ICMS destacado</th>
                <th className="px-3 py-2.5 font-medium">Situação</th>
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-linha">
              {d?.itens.map((doc) => (
                <tr key={doc.id} className={`align-top ${doc.situacao === 'CANCELADO' || doc.situacao === 'DENEGADO' ? 'text-tinta-3' : ''}`}>
                  <td className="num px-3 py-2">
                    {dataBr(doc.dataEmissao)}
                    {doc.competencia !== doc.dataEmissao.slice(0, 7) && <span className="block text-[11px] text-tinta-3">comp. {doc.competencia}</span>}
                  </td>
                  <td className="px-3 py-2">
                    <span className="font-medium">{ROTULO_MODELO[doc.modelo] ?? doc.modelo}</span> <span className="num">{doc.numero}</span>
                    {doc.ibsCbsDeclarado && (
                      <span className="mt-0.5 block">
                        <Selo tom="navy">IBS/CBS</Selo>
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <Selo tom={doc.papel === 'ENTRADA' ? 'navy' : 'neutro'}>{ROTULO_PAPEL[doc.papel]}</Selo>
                  </td>
                  <td className="min-w-0 px-3 py-2">
                    <Contraparte doc={doc} />
                  </td>
                  <td className="num px-3 py-2 text-right">{reais(doc.valorTotal)}</td>
                  <td className="px-3 py-2 text-right">
                    <Icms doc={doc} />
                  </td>
                  <td className="px-3 py-2">
                    {doc.situacao === 'AUTORIZADO' ? (
                      <span className="text-xs text-tinta-2">Autorizada</span>
                    ) : (
                      <Selo tom={doc.situacao === 'SEM_PROTOCOLO' ? 'neutro' : 'alerta'}>{ROTULO_SITUACAO[doc.situacao]}</Selo>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <a href={`/documentos/${doc.id}/xml`} className="text-xs text-navy underline-offset-4 hover:underline" title="Baixar o XML original">
                      XML
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {d && d.total > POR_PAGINA && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-linha px-4 py-2.5 text-xs text-tinta-2">
              <span className="num">
                Página {pagina} de {paginas}
              </span>
              <div className="flex gap-2">
                <Botao variante="fantasma" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>
                  Anterior
                </Botao>
                <Botao variante="fantasma" disabled={pagina >= paginas} onClick={() => setPagina((p) => p + 1)}>
                  Próxima
                </Botao>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

type Doc = {
  papel: string
  emitenteNome: string | null
  destinatarioNome: string | null
  contraparteTipo: string | null
  contraparteDoc: string | null
  estabelecimentoCnpj: string
  icms: number
  icmsSt: number
  icmsCreditoSn: number
}

function Contraparte({ doc }: { doc: Doc }) {
  const nome = doc.papel === 'ENTRADA' ? doc.emitenteNome : doc.destinatarioNome
  return (
    <span className="block min-w-0">
      <span className="block truncate">{nome ?? (doc.contraparteTipo === 'CPF' ? 'Pessoa física' : '—')}</span>
      <span className="num block truncate text-[11px] text-tinta-3">
        {doc.contraparteTipo === 'CPF' ? 'CPF protegido' : doc.contraparteDoc ? formatarCnpj(doc.contraparteDoc) : ''}
        {` · unid. ${formatarCnpj(doc.estabelecimentoCnpj).slice(-8)}`}
      </span>
    </span>
  )
}

/** ICMS próprio destacado; embaixo, ST e o crédito de emitente do Simples quando houver. */
function Icms({ doc }: { doc: Doc }) {
  const nada = !doc.icms && !doc.icmsSt && !doc.icmsCreditoSn
  return (
    <span className="num block">
      <span className={nada ? 'text-tinta-3' : ''}>{reais(doc.icms)}</span>
      {doc.icmsSt > 0 && <span className="block whitespace-nowrap text-[11px] text-tinta-3">ST {reais(doc.icmsSt)}</span>}
      {doc.icmsCreditoSn > 0 && <span className="block whitespace-nowrap text-[11px] text-tinta-3">créd. Simples {reais(doc.icmsCreditoSn)}</span>}
    </span>
  )
}

function Resumo({ rotulo, valor, nota }: { rotulo: string; valor: number; nota: string }) {
  return (
    <div className="rounded-lg border border-linha bg-painel p-4">
      <p className="text-[11px] uppercase tracking-[0.06em] text-tinta-3">{rotulo}</p>
      <p className="num mt-1 text-lg font-semibold">{reais(valor)}</p>
      <p className="text-[11px] text-tinta-3">{nota}</p>
    </div>
  )
}
